const HEARTBEAT_PATH = "/healthz";
const HEARTBEAT_INTERVAL_MS = 20000;
const HEARTBEAT_TIMEOUT_MS = 10000;
const SLOW_THRESHOLD_MS = 3500;
const FAILURE_THRESHOLD = 2;
const SLOW_THRESHOLD_COUNT = 4;
const DISMISS_DURATION_MS = 10 * 60 * 1000;
const DISMISS_UNTIL_KEY = "xtracker.networkHealth.dismissUntil";

let heartbeatTimer = null;
let consecutiveFailures = 0;
let consecutiveSlowResponses = 0;
let currentState = "healthy";

function ensureBannerElement() {
  let banner = document.getElementById("network-health-banner");
  if (banner) return banner;

  banner = document.createElement("div");
  banner.id = "network-health-banner";
  banner.className = "hidden fixed top-4 left-1/2 -translate-x-1/2 z-[9999] max-w-2xl w-[calc(100%-2rem)] rounded-lg border px-4 py-3 shadow-lg";
  banner.setAttribute("role", "alert");
  banner.innerHTML = `
    <div class="flex items-start gap-3">
      <div id="network-health-icon" class="text-lg leading-none">⚠️</div>
      <div class="flex-1">
        <p id="network-health-title" class="font-semibold"></p>
        <p id="network-health-message" class="text-sm mt-1"></p>
        <button
          type="button"
          id="network-health-dismiss"
          class="mt-2 inline-flex items-center rounded border border-current/30 px-2 py-1 text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5"
        >
          Dismiss for 10 minutes
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(banner);

  const dismissButton = banner.querySelector("#network-health-dismiss");
  if (dismissButton) {
    dismissButton.addEventListener("click", dismissForTenMinutes);
  }

  return banner;
}

function dismissForTenMinutes() {
  try {
    const dismissUntil = Date.now() + DISMISS_DURATION_MS;
    localStorage.setItem(DISMISS_UNTIL_KEY, String(dismissUntil));
  } catch (error) {
    // Ignore storage errors and just hide for current view
  }

  hideBanner();
}

function dismissalActive() {
  try {
    const raw = localStorage.getItem(DISMISS_UNTIL_KEY);
    if (!raw) return false;

    const dismissUntil = parseInt(raw, 10);
    if (Number.isNaN(dismissUntil)) {
      localStorage.removeItem(DISMISS_UNTIL_KEY);
      return false;
    }

    if (Date.now() < dismissUntil) {
      return true;
    }

    localStorage.removeItem(DISMISS_UNTIL_KEY);
    return false;
  } catch (error) {
    return false;
  }
}

function setBannerStyle(state) {
  const banner = ensureBannerElement();
  const icon = document.getElementById("network-health-icon");

  banner.classList.remove(
    "hidden",
    "bg-red-50",
    "border-red-300",
    "text-red-900",
    "dark:bg-red-900/30",
    "dark:border-red-700",
    "dark:text-red-100",
    "bg-yellow-50",
    "border-yellow-300",
    "text-yellow-900",
    "dark:bg-yellow-900/30",
    "dark:border-yellow-700",
    "dark:text-yellow-100"
  );

  if (state === "degraded") {
    banner.classList.add(
      "bg-yellow-50",
      "border-yellow-300",
      "text-yellow-900",
      "dark:bg-yellow-900/30",
      "dark:border-yellow-700",
      "dark:text-yellow-100"
    );
    if (icon) icon.textContent = "⚠️";
    return;
  }

  banner.classList.add(
    "bg-red-50",
    "border-red-300",
    "text-red-900",
    "dark:bg-red-900/30",
    "dark:border-red-700",
    "dark:text-red-100"
  );
  if (icon) icon.textContent = "🚨";
}

function showState(state, title, message) {
  currentState = state;

  if (dismissalActive()) {
    return;
  }

  setBannerStyle(state);

  const titleElement = document.getElementById("network-health-title");
  const messageElement = document.getElementById("network-health-message");

  if (titleElement) titleElement.textContent = title;
  if (messageElement) messageElement.textContent = message;
}

function hideBanner() {
  const wasUnhealthy = currentState !== "healthy";
  currentState = "healthy";
  const banner = document.getElementById("network-health-banner");
  if (!banner) return;
  banner.classList.add("hidden");

  if (wasUnhealthy) {
    window.dispatchEvent(new CustomEvent("network:recovered"));
  }
}

function timeoutSignal(timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return { controller, timer };
}

function heartbeatRequest() {
  const { controller, timer } = timeoutSignal(HEARTBEAT_TIMEOUT_MS);
  const startedAt = Date.now();

  return fetch(HEARTBEAT_PATH, {
    method: "HEAD",
    cache: "no-store",
    credentials: "same-origin",
    signal: controller.signal,
    headers: { "X-Requested-With": "XMLHttpRequest" }
  })
    .then((response) => {
      clearTimeout(timer);
      const duration = Date.now() - startedAt;
      return { ok: response.ok, duration };
    })
    .catch((error) => {
      clearTimeout(timer);
      return { ok: false, duration: HEARTBEAT_TIMEOUT_MS, error };
    });
}

function connectionHint() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) return "";

  const effectiveType = connection.effectiveType ? ` (${connection.effectiveType})` : "";
  return ` Connection appears weak${effectiveType}.`;
}

async function evaluateNetworkHealth() {
  if (!navigator.onLine) {
    consecutiveFailures += 1;
    showState(
      "offline",
      "No internet connection detected",
      "Your device appears offline. Check Wi-Fi/cable/router and reconnect before continuing."
    );
    return;
  }

  const result = await heartbeatRequest();

  if (!result.ok) {
    consecutiveFailures += 1;
    if (consecutiveFailures >= FAILURE_THRESHOLD) {
      showState(
        "unreachable",
        "Connection to XTracker is unavailable",
        "We cannot reach XTracker right now. Check your internet/router first, then retry. If other websites also fail, contact your ISP/local IT."
      );
    }
    return;
  }

  consecutiveFailures = 0;

  if (result.duration >= SLOW_THRESHOLD_MS) {
    consecutiveSlowResponses += 1;
  } else {
    consecutiveSlowResponses = 0;
  }

  if (consecutiveSlowResponses >= SLOW_THRESHOLD_COUNT) {
    showState(
      "degraded",
      "Network connection is unstable",
      `Requests are slower than normal.${connectionHint()} Please stabilize your internet and retry before contacting XTracker support.`
    );
    return;
  }

  if (currentState !== "healthy") {
    hideBanner();
  }
}

function startHeartbeatLoop() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
  }

  evaluateNetworkHealth();
  heartbeatTimer = setInterval(evaluateNetworkHealth, HEARTBEAT_INTERVAL_MS);
}

function initializeNetworkHealthMonitor() {
  if (window.__networkHealthMonitorInitialized) return;
  window.__networkHealthMonitorInitialized = true;

  document.addEventListener("online", () => {
    consecutiveFailures = 0;
    startHeartbeatLoop();
  });

  document.addEventListener("offline", () => {
    showState(
      "offline",
      "No internet connection detected",
      "Your device appears offline. Check Wi-Fi/cable/router and reconnect before continuing."
    );
  });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      evaluateNetworkHealth();
    }
  });

  startHeartbeatLoop();
}

document.addEventListener("turbo:load", initializeNetworkHealthMonitor);
document.addEventListener("DOMContentLoaded", initializeNetworkHealthMonitor);
